import React, { useState, useEffect, useContext } from 'react';
import { Trophy, Award, User, Calendar, MessageSquare, FileText, HelpCircle, CheckCircle, X } from 'lucide-react';
import AuthContext from '../contexts/AuthContext';
import ProfilePicture from './ProfilePicture';
import { get } from '../services/apiClient';

const MeritLeaderboard = ({ isOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && user?.school?.id) {
      fetchLeaderboard();
    }
  }, [isOpen, user?.school?.id]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/api/schools/${user.school.id}/merit-leaderboard?limit=50`);
      setLeaderboard(response.leaderboard || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getMeritColor = (merit) => {
    if (merit >= 1000) return 'text-purple-600 dark:text-purple-400';
    if (merit >= 500) return 'text-blue-600 dark:text-blue-400';
    if (merit >= 250) return 'text-green-600 dark:text-green-400';
    if (merit >= 100) return 'text-yellow-600 dark:text-yellow-400';
    if (merit >= 50) return 'text-orange-600 dark:text-orange-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getMeritBadgeColor = (merit) => {
    if (merit >= 1000) return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    if (merit >= 500) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    if (merit >= 250) return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
    if (merit >= 100) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400';
    if (merit >= 50) return 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400';
    return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Trophy className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Trophy className="h-5 w-5 text-amber-600" />;
    return <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-5 text-center">#{rank}</span>;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const currentUserRank = leaderboard.findIndex(entry => entry.id === user?.id) + 1;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Award className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Merit Leaderboard - {user?.school?.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Current User Rank */}
        {currentUserRank > 0 && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Your current rank: <span className="font-bold">#{currentUserRank}</span> with{' '}
              <span className="font-bold">{user?.merit || 0}</span> merit points
            </p>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={fetchLeaderboard}
                className="mt-2 text-blue-600 dark:text-blue-400 hover:underline"
              >
                Try again
              </button>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No users with merit points yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {leaderboard.map((entry) => (
                <div 
                  key={entry.id} 
                  className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${
                    entry.id === user?.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Rank */}
                    <div className="flex-shrink-0 w-8 flex justify-center">
                      {getRankIcon(entry.rank)}
                    </div>

                    {/* Profile Picture */}
                    <div className="flex-shrink-0">
                      <ProfilePicture 
                        url={entry.profilePictureUrl}
                        alt={entry.name}
                        size="sm"
                      />
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {entry.name}
                          {entry.id === user?.id && (
                            <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(You)</span>
                          )}
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          Member since {formatDate(entry.memberSince)}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <FileText className="h-3 w-3 mr-1" />
                          {entry.stats.materials} materials
                        </div>
                        <div className="flex items-center">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {entry.stats.comments} comments
                        </div>
                        <div className="flex items-center">
                          <HelpCircle className="h-3 w-3 mr-1" />
                          {entry.stats.questions} questions
                        </div>
                        <div className="flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {entry.stats.answers} answers
                        </div>
                      </div>
                    </div>

                    {/* Merit Score */}
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getMeritBadgeColor(entry.merit)}`}>
                        <Award className="h-4 w-4 mr-1" />
                        {entry.merit}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
          <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
            Merit is earned by contributing materials, comments, questions, and answers that receive positive votes from the community.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MeritLeaderboard;